/**
 * Where a mid-session 401 should send the browser (issue #154).
 *
 * The clinician app and the patient portal have SEPARATE sign-in pages. A 401 on
 * a portal route must return the patient to the branded `/portal/signin` (never
 * the clinician `/signin`), and vice-versa, preserving where they were via
 * `?redirect=`. Pure so it is unit-testable without a browser.
 */
export interface RedirectLocation {
  pathname: string;
  search: string;
  hash: string;
}

/** True for the portal route space (`/portal` and everything under it). */
export function isPortalPath(pathname: string): boolean {
  return pathname === '/portal' || pathname.startsWith('/portal/');
}

/**
 * The sign-in URL to redirect to, or `null` when already on the correct sign-in
 * page (so we don't loop). Portal sessions → `/portal/signin`, else `/signin`.
 */
export function resolveSignInRedirect(location: RedirectLocation): string | null {
  const signInPath = isPortalPath(location.pathname) ? '/portal/signin' : '/signin';
  if (location.pathname === signInPath) {
    return null;
  }
  const here = location.pathname + location.search + location.hash;
  return `${signInPath}?redirect=${encodeURIComponent(here)}`;
}

/**
 * Validate a post-login redirect for the PORTAL: only a portal-relative path is allowed. Rejects
 * open-redirect vectors — absolute URLs, protocol-relative `//host`, schemes, and backslash tricks —
 * and any non-`/portal` destination. Returns the safe path or `null`.
 */
export function sanitizePortalRedirect(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/')) {
    return null;
  }
  if (raw.startsWith('//') || raw.includes('\\')) {
    return null;
  }
  const pathname = raw.split(/[?#]/, 1)[0];
  return isPortalPath(pathname) ? raw : null;
}

interface FromLocation {
  pathname?: string;
  search?: string;
  hash?: string;
}

/**
 * The destination after a successful portal login: the guard's stashed `from` location
 * (`location.state.from`, preserving search + hash), else the `?redirect=` param a 401 wrote — each
 * validated as portal-relative — else the portal home. Mirrors the clinician sign-in's behavior.
 */
export function portalPostLoginDestination(opts: { state: unknown; search: string }): string {
  const from = (opts.state as { from?: FromLocation } | null)?.from;
  const fromPath = from?.pathname ? from.pathname + (from.search ?? '') + (from.hash ?? '') : undefined;
  const redirectParam = new URLSearchParams(opts.search).get('redirect');
  return sanitizePortalRedirect(fromPath) ?? sanitizePortalRedirect(redirectParam) ?? '/portal';
}
